import {makeAutoObservable, observable} from 'mobx'
import {observer} from 'mobx-react-lite'
import React from 'react'
import {Transition} from 'react-transition-group'
import tw from 'tailwind-styled-components'
import {Link} from '@components/common/button/button'
import {ShevronIcon, TickIcon} from '@components/common/icons/icons'
import {flattenTreeNodes, TreeNode} from '@components/common/tree/tree-node'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {createUseStore, noPropagate, useMemoOne} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import TriangleIcon from './triangle.svg'

interface State<T> {
  currentLevels: T[]
  transition?: 'left' | 'right'
  selectedValue?: T
  previousValue?: T
}

class Store<T> {
  state: State<T> = {
    currentLevels: []
  }

  props: FlatTreeProps<T>

  constructor() {
    makeAutoObservable(this, {
      props: observable.shallow
    })
  }

  setProps(props: FlatTreeProps<T>) {
    this.props = props
  }

  get nodesMap() {
    return new Map(flattenTreeNodes(this.props.nodes).map(x => [x.value, x]))
  }

  getNode(value: T) {
    return this.nodesMap.get(value)
  }

  getChildren(value: T | undefined) {
    const node = value && this.getNode(value)
    return node === undefined ? this.props.nodes : node.children ?? []
  }

  setSelectedValue(selectedValue: T | undefined) {
    this.state.previousValue = this.state.selectedValue
    this.state.selectedValue = selectedValue
  }

  setCurrentLevels(currentLevels: T[]) {
    this.state.transition =
      currentLevels.length > this.state.currentLevels.length ? 'right' : 'left'
    this.state.currentLevels = currentLevels
  }

  resetTransition() {
    this.state.transition = undefined
  }

  updateSelectedValue(selectedValue: T | undefined) {
    if (selectedValue === undefined) {
      this.setSelectedValue(selectedValue)
      this.setCurrentLevels([])
      return
    }
    const index = this.state.currentLevels.indexOf(selectedValue)
    if (index >= 0) {
      this.setSelectedValue(selectedValue)
      this.setCurrentLevels(this.state.currentLevels.slice(0, index + 1))
    } else {
      if (this.getNode(selectedValue)?.selectable) {
        this.props.onChange(selectedValue)
      } else {
        this.setSelectedValue(selectedValue)
        this.setCurrentLevels([...this.state.currentLevels, selectedValue])
      }
    }
  }

  get levels() {
    return this.props.levels.map((label, i) => {
      const isCurrent = this.state.currentLevels.length === i
      const isBeforeCurrent = this.state.currentLevels.length === i + 1
      return {
        node:
          i && i < this.state.currentLevels.length
            ? this.state.currentLevels[i - 1]
            : undefined,
        label: `${i + 1}. ${isCurrent ? label : ''}`,
        isLast: i === this.props.levels.length - 1,
        isBeforeCurrent,
        selectable: i < this.state.currentLevels.length,
        isCurrent
      }
    })
  }
}

const [Context, useStore] = createUseStore(Store)

const TwLevel = tw.div`
    border 
    border-l-0 
    border-r-0 
    border-solid 
    border-main-old 
    flex 
    items-center
`

const Pager = observer(() => {
  const [, store] = useStore()
  return (
    <div className="flex text-12 h-32 pr-4">
      {store.levels.map((x, i) => {
        return (
          <React.Fragment key={i}>
            <TwLevel
              className={cn(
                x.selectable && 'cursor-pointer',
                (x.selectable || !x.isCurrent) && 'bg-header-old',
                x.isCurrent && 'flex-1 font-semibold'
              )}
            >
              {x.selectable ? (
                <Link
                  className="px-8"
                  onClick={e => {
                    e.stopPropagation()
                    store.updateSelectedValue(x.node)
                  }}
                >
                  {x.label}
                </Link>
              ) : (
                <div onClick={noPropagate} className="px-8">
                  {x.label}
                </div>
              )}
            </TwLevel>

            <TriangleIcon
              className={cn(
                !x.isLast &&
                  'border-0 border-t border-b border-solid border-main-old',
                x.isLast || x.isBeforeCurrent ? 'bg-main-old' : 'bg-header-old',
                x.selectable || !x.isCurrent
                  ? 'text-bg-header-old'
                  : 'text-bg-main-old'
              )}
            />
          </React.Fragment>
        )
      })}
    </div>
  )
})

export interface FlatTreeProps<T> {
  nodes: TreeNode<T>[]
  width: number
  value?: T
  onChange: (value: T) => void
  className?: string
  nodesClassName?: string
  levels: string[]
}

const TwNode = tw.div`
    group 
    h-32 
    px-4 
    flex 
    items-center 
    cursor-pointer 
    overflow-hidden 
    whitespace-nowrap 
    text-ellipsis 
    border-solid 
    border-b 
    border-0
    border-lists-divider-old
    hover:bg-main-even-old
`

const NodesContainer = observer(() => {
  const [state, store] = useStore()
  const nodeRef = React.useRef(null)
  return (
    <div className="relative overflow-hidden">
      <Nodes
        value={state.transition ? state.previousValue : state.selectedValue}
      />
      <Transition
        nodeRef={nodeRef}
        in={!!state.transition}
        mountOnEnter
        exit={false}
        unmountOnExit
        timeout={{enter: 300}}
        onEntered={() => {
          store.resetTransition()
        }}
      >
        {x => (
          <Nodes
            ref={nodeRef}
            className={cn(
              'absolute top-0 transition transform ease-main duration-300',
              x === 'entering'
                ? 'translate-x-0'
                : state.transition === 'right'
                  ? 'translate-x-full'
                  : '-translate-x-full'
            )}
            value={state.selectedValue}
          />
        )}
      </Transition>
    </div>
  )
})

const Nodes = observer(
  React.forwardRef(
    <T,>(
      props: {value: T; className?: string},
      ref: React.Ref<HTMLDivElement>
    ) => {
      const [, store] = useStore()
      const nodes = store.getChildren(props.value)
      return (
        <div
          ref={ref}
          style={{width: store.props.width}}
          className={cn(
            'bg-main-old',
            store.props.nodesClassName,
            props.className
          )}
        >
          <WithPerfectScrollbar>
            {nodes.map((node, i) => {
              const isCurrent = node.value === store.props.value
              return (
                <TwNode
                  key={i}
                  onClick={e => {
                    e.stopPropagation()
                    store.updateSelectedValue(node.value)
                  }}
                >
                  <div className="shrink-0 inline-flex">{node.icon}</div>
                  <div className="ml-4 flex-1 text-no-wrap">{node.label}</div>
                  <div className="shrink-0 inline-flex">
                    {node.selectable ? (
                      <TickIcon
                        className={cn(
                          'text-obscure-old group-hover:text-action-hover group-hover:visible',
                          !isCurrent && 'invisible'
                        )}
                      />
                    ) : (
                      <ShevronIcon className="text-action-default" />
                    )}
                  </div>
                </TwNode>
              )
            })}
          </WithPerfectScrollbar>
        </div>
      )
    }
  )
)

export const FlatTree = <T,>(props: FlatTreeProps<T>) => {
  const store = useMemoOne(() => new Store(), [])
  store.setProps(props)
  return (
    <Context.Provider value={store}>
      <div className={cn('cursor-default', props.className)}>
        <Pager />
        <NodesContainer />
      </div>
    </Context.Provider>
  )
}
